import BaseModal from 'components/BaseModal'
import WizardFormV2 from 'components/WizardFormV2'
import { MODAL_SIZE_MEDIUM } from 'constants/Layout'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import ProjectAPI from 'restAPI/ProjectAPI';
import UserAPI from 'restAPI/UserAPI';
import _ from 'lodash';
import { editorRules, FieldTypes, nameRegexValidator, NAME_REGEX_START_WITH_NUMBER, urlRegexValidator, parseSelectOptions } from 'utils/formUtils';
import { getOwnerFields } from '../common';
import makeCancellablePromise from "make-cancellable-promise";
import Vocabulary, { getVocabulary } from 'components/Vocabulary';
import message from 'components/CustomMessage';
import { marked } from 'marked';
import htmlToMarkDown, { sanitizeMarkdown } from 'utils/htmlToMarkDownUtil';
import { OWNER_FIELDS, PROJECT_STATUSES, PROJECT_TYPES, STATUS_SUBMITTED, EXCEPTION_PROJECT_STATUSES, STATUS_DRAFT } from './Constants';
import { isTypeException, isTypeExceptionPresent } from './utils';
import moment from 'moment';
import { useGlobalUserListContext } from 'contexts/GlobalUserListContext';
import ACCommentEditor from 'ac-components/ac-comment-editor/ACCommentEditor';
import { convertCommentToNewFormat } from 'views/Findings/FindingDetails/common';

const ValueToIdList = [...OWNER_FIELDS];

const getUndefindOwners = () => {
    const obj = {};
    _.forEach(OWNER_FIELDS, ownerKey => {
        obj[ownerKey] = undefined 
    });
    return obj;
};

export default forwardRef(function ProjectFormPopup({ editId, activeUser, ...props }, ref) {
    const thisRef = useRef();
    const [form, setForm] = useState();
    const [users, setUsers] = useState();
    const [projectTypes, setProjectTypes] = useState(() => {
        return parseSelectOptions(PROJECT_TYPES)
    });
    const [isStartDateMandatory, setIsStartDateMandatory] = useState(false);
    const [existingProject, setExistingProject] = useState();
    const [createdProject, setCreatedProject] = useState();
    const initialValues = {};
    const [loading, setLoading] = useState(false);
    const projectName = getVocabulary("project")
    const validateAPIRef = useRef();
    const { refreshUsers } = useGlobalUserListContext();

    const fetchUsers = useCallback((showLoader = true) => {
        showLoader && setLoading(true);
        const { promise, cancel } = UserAPI.getAll(/*selectedProduct || productId || existingProject?.product*/);
        const promise1 = promise.then(data => {
            const userMap = data.map(d => ({ value: d.id, label: d.displayName }));
            setUsers(userMap);
            return userMap;
        }, _.noop).finally(() => {
            showLoader && setLoading(false);
        });
        return { promise: promise1, cancel };
    }, [/*productId, selectedProduct, existingProject*/]);

    useEffect(() => {
        return fetchUsers().cancel;
    }, [fetchUsers]);

    useEffect(() => {
        if (editId) {
            const { promise, cancel } = makeCancellablePromise(ProjectAPI.get(editId));
            promise.then(updateDataAfterGet).then(data => {
                // convert markdown to html
                data.description = convertCommentToNewFormat(marked.parse(data.description));
                setExistingProject(data);
                const isExceptionSelected = !_.isEmpty(data.type) && isTypeExceptionPresent(data.type);
                if (!_.isEmpty(data.type)) {
                    setProjectTypes(projectTypes => _.map(projectTypes, (entry) => {
                        const _isTypeException = isTypeException(entry.label);
                        return {
                            ...entry,
                            disabled: ((isExceptionSelected && !_isTypeException) || (!isExceptionSelected && _isTypeException))
                        };
                    }));
                }
            }, _.noop);
            return cancel;
        }
    }, [editId, /*subProductOnProduct*/]);

    useImperativeHandle(ref, () => ({ ...thisRef.current }));

    const onSuccess = useCallback((data) => {
        setCreatedProject(data);
        const isExceptionSelected = isTypeException(data.type);
        const isSubmittedStatus = [STATUS_SUBMITTED].includes(data?.status);
        const isPreviousStatusIsSubimitted = [STATUS_SUBMITTED].includes(existingProject?.status);
        const messageExceptionSubmitted = <>Approval workflow has been initiated for your <Vocabulary name= "exception"/>.</>
       
        message.success(
            <>{!editId ? "Created" : "Edited"} {projectName.toLowerCase()} - {data.name}</>
        );
        if(isExceptionSelected && isSubmittedStatus && !isPreviousStatusIsSubimitted) message.success(messageExceptionSubmitted);       
        thisRef.current.close();
    }, [editId, projectName, existingProject]);

    const onChange = useCallback((values, allValues) => {
        if (_.has(values, "type")) {
            const isExceptionSelected = isTypeExceptionPresent(values.type);
            const selecedTypeLength = (values.type || []).length;

            if (isExceptionSelected) {
                form.setFieldsValue({
                    status: STATUS_DRAFT
                });
            } else if (EXCEPTION_PROJECT_STATUSES.indexOf(allValues.status) !== -1) {
                form.setFieldsValue({
                    status: undefined
                });
            }
            
            setProjectTypes(projectTypes => _.map(projectTypes, (entry) => {
                const _isTypeException = isTypeException(entry.label);
                return {
                    ...entry,
                    disabled: selecedTypeLength > 0 &&
                        ((isExceptionSelected && !_isTypeException) || (!isExceptionSelected && _isTypeException))
                };
            }));
        }
        if (_.has(values, "endDate")) {
            if (values.endDate) {
                setIsStartDateMandatory(true);
            } else {
                setIsStartDateMandatory(false);
            }
        }
        if (form) {
            if (_.has(values, 'product')) {
                form.setFieldsValue({
                    subProduct: undefined, 
                    environment: undefined,
                    approvers: undefined,
                    subscriberUserIds: undefined
                });
            } else if (_.has(values, 'subProduct')) {
                form.setFieldsValue({
                    environment: undefined
                });
            }
        }
    }, [form]);
    
    function getBasicInfoFields() {
        return [{
            name: "name",
            label: <><Vocabulary name="project" /> Name</>,
            colSpan: 12,
            NoDefaultLengthRule: true,
            props: {
                autoFocus: true
            },
            rules: [{
                required: true,
                message: <>Enter a <Vocabulary name="project" isLowerCase={true} /> name.</>
            }, nameRegexValidator({ regex: NAME_REGEX_START_WITH_NUMBER }),
            {
                validator: (rule, value) => {
                    if(typeof value === 'string' && value?.trim().length !== 0) {
                        const remains = value.split(NAME_REGEX_START_WITH_NUMBER).filter(Boolean).length;
                        if ((existingProject?.name.toLowerCase() !== value.toLowerCase() && value.trim().length < 50) && remains === 0) {
                            // cancel the api
                            if (validateAPIRef.current) {
                                validateAPIRef.current();
                            }

                            const { promise, cancel } = ProjectAPI.validate({ name: value });
                           
                            validateAPIRef.current = cancel;

                            return promise.catch(res => Promise.reject('Name already exists.'));
                        }                            
                    }
                    return Promise.resolve();
                }
            }
            ],
        },
        {
            name: "type",
            label: "Type",
            colSpan: 12,
            type: FieldTypes.MULTI_SELECT,
            props: { allowClear: true },
            options: projectTypes
        },
        {
            name: "description",
            label: <span className='required-mark'>Description</span>,
            type: FieldTypes.TEXT_AREA,
            NoDefaultLengthRule: true,
            valuePropName: "initialValue",
            render: () => {
                return (
                    <ACCommentEditor placeholder={'Description'} showFooter={false} defaultFocus={true} users={[]} />
                );
            },
            rules: [editorRules({maxMessage: true})]
        }
    ]
    }

    const advancedFields = useMemo(() => {
        return [{
            name: "startDate",
            label: "Start Date",
            type: FieldTypes.DATE,
            colSpan: 6,
            required: isStartDateMandatory,
            props: {
                disabledDate: (current, value) => {
                    return form?.getFieldValue("endDate") && current && current.isAfter(form.getFieldValue("endDate"), 'day');
                }
            }
        }, {
            name: "endDate",
            label: "End Date",
            type: FieldTypes.DATE,
            colSpan: 6,
            props: {
                disabledDate: (current, value) => {
                    return form?.getFieldValue("startDate") && current && current.isBefore(form.getFieldValue("startDate"), 'day');
                }
            }
        }, {
            name: "status",
            label: "Status",
            type: FieldTypes.SELECT,
            colSpan: 12,
            options: PROJECT_STATUSES
        }, {
            name: "repoLink",
            label: "Repo link",
            rules:[urlRegexValidator()]
        },
        // {
        //     name: "createTicket",
        //     label: "Create JIRA Ticket?",
        //     type: FieldTypes.BOOLEAN,
        //     inline: true,
        //     disabled: true
        // },
        //  {
        //     name: "tags",
        //     label: "Tags",
        //     type: "Tags",
        //     props: {
        //         onChange: makeTagsLowerCase,
        //     },
        // }, 
        {
            name: "notes",
            label: "Notes",
            type: FieldTypes.TEXT_AREA,
            customLengthRule: {
                max: 10000,
                message: "Maximum length is 10000 characters" 
            }
        }
        ];
    }, [isStartDateMandatory, form]);

    const pages = [{
        name: "Basic Information",
        fields: getBasicInfoFields()
    }, {
        name: "Advanced",
        fields: advancedFields
    }, 
    {
        name: "Owners",
        fields: getOwnerFields({ users, activeUser, form, fetchUsers, loading, showTeamView: false, refreshUsers })
    }
    ]
    return (
        <BaseModal
            modalProps={{
                footer: null,
                width: MODAL_SIZE_MEDIUM,
                style: { top: 80 },
                title: <> {!editId ? "Add" : "Edit"} <Vocabulary name="project" /></>
            }}
            ref={thisRef}
            showExitWarning
            afterClose={() => props.afterClose(createdProject)}
        >
            <WizardFormV2
                isEdit={!_.isUndefined(editId)}
                onCancel={thisRef.current?.close}
                pages={pages}
                initialValues={editId ?
                    {
                        ...initialValues,
                        ...existingProject,
                    } :
                    {
                        ...initialValues,
                    }}
                formRef={setForm}
                api={ProjectAPI[!editId ? "save" : "update"]}
                beforeAPI={updateDataBeforePush}
                onChange={onChange}
                onSuccess={onSuccess}
                validator={parseErrorMessage}
                loading={editId && _.isEmpty(existingProject)}
                showSkipSaveBtnFromStep={0}
            />
        </BaseModal>
    )
});

function parseErrorMessage(e) {
    const message = _.lowerCase(e.response?.data?.message ?? "");
    if (message.startsWith("Project with name")) {
        return [{
            name: "name",
            errors: [message]
        }]
    }
}

const updateDataBeforePush = (data) => {
    ValueToIdList.forEach(v => {
        if (data[v] && v !== 'subProduct') {
            data[v] = {
                id: data[v]
            }
        }
        // if(v === 'subProduct') {
        //     data[v] = data[v].map(id => ({id}));
        // }
    });

    const isExceptionSelected = isTypeExceptionPresent(data.type);
    data = {
        ...data,
        ...(isExceptionSelected ? getUndefindOwners() : {})
    }

    data.startDate = data.startDate || null;
    data.endDate = data.endDate || null;

    const approvers = _.map(data.approvers, (entry, index) => ({ userId: entry, sequence: index }));
    data.exceptionDetails = {
        id: data.exceptionDetails?.id,
        approvers: !_.isEmpty(approvers) ? approvers : undefined,
        subscriberUserIds: data.subscriberUserIds,
        projectScopeReqDto: {
            productId: data.product,
            subProductId: data.subProduct,
            envName: data.environment
        }
    }

    if (EXCEPTION_PROJECT_STATUSES.indexOf(data.status) !== -1 && !isExceptionSelected) {
        data.status = null;
    }

    if (data.type) {
        data.type = data.type.join(",");
    }

    // convert html to markdown
    data.description = sanitizeMarkdown(htmlToMarkDown(data.description));

    return _.omit(data, ['approvers', 'subscriberUserIds', 'product', 'subProduct', 'environment']);
};

const updateDataAfterGet = (data) => {
    ValueToIdList.forEach(v => {
        if (data[v] && v !== 'subProduct') {
            data[v] = data[v].id;
        }
        // if(v === 'subProduct') {
        //     data[v] = data[v].map(subprod => subprod.id);
        // }
    });

    data.approvers = [];
    _.forEach(data.exceptionDetails?.approvers, entry => {
        data.approvers[entry.sequence] = entry.userId;
    });
    data.approvers = _.compact(data.approvers);
    data.subscriberUserIds = data.exceptionDetails?.subscriberUserIds;

    if (data.exceptionDetails?.projectScope) {
        const { product, subProduct, env } = data.exceptionDetails.projectScope;
        data.product = product.id;
        data.subProduct = subProduct.id;
        data.environment = env.name;
    }

    if(data.startDate){
        data.startDate = moment(data.startDate);
    }
    if(data.endDate){
        data.endDate = moment(data.endDate);
    }

    if(data.type){
        data.type = data.type.split(",");
    }
    else
    data.type = []
    return data;
};